import React, {useEffect, useRef, useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {observer} from 'mobx-react-lite';

import {useModalDimensions} from '../../hooks/useModalDimensions';
import {useStores} from '../../stores/use-stores';
import theme from '../../style/theme';
import Button from '../themed/Button';
import CrossPlatformModal from '../ui-controls/CrossPlatformModal';
import TextInput from '../ui-controls/form/TextInput';
import Text from '../ui-controls/Text';

const CouponModal = observer(({open, callback}: {open: boolean; callback?: () => void}) => {
  const {cartStore} = useStores();
  const [visible, setVisible] = useState(false);
  const [coupon, setCoupon] = useState(cartStore.currentOrder?.coupon_code || null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const {containerStyle: modalContainerStyle} = useModalDimensions({size: 'large'});
  /**
   * Mirrors `error` synchronously. A refused coupon is detached by the store,
   * which reloads the order and replaces `currentOrder` — so the sync effect
   * below fires in the same commit that renders the message and would otherwise
   * wipe both the message and the code the shopper typed.
   */
  const errorRef = useRef<string | null>(null);
  const showError = (message: string | null) => {
    errorRef.current = message;
    setError(message);
  };
  /**
   * True once the shopper has edited the field this time the modal was opened.
   * Their text outranks the server's applied code until they submit or reopen.
   */
  const dirtyRef = useRef(false);

  const appliedCoupon = cartStore.currentOrder?.coupon_code ?? null;
  /**
   * DIS-1648: re-seed the field from the order's applied code. Keyed on the code
   * itself, never on `currentOrder`, whose identity is replaced by every
   * `PUT /ecommerce/order` — measured in a browser run: incrementing a cart line,
   * then typing `GOODBYE`, set `coupon` back to the order's (absent) code 1.0 s
   * later, which left `ADD COUPON CODE` permanently disabled with `GOODBYE` still
   * on screen. `?? null` keeps the input controlled; `undefined` made React drop
   * the value prop, so the typed text stayed visible over empty state.
   */
  useEffect(() => {
    if (errorRef.current || dirtyRef.current) return;
    setCoupon(appliedCoupon);
  }, [appliedCoupon]);

  const onClose = () => {
    if (callback) {
      callback();
    }
    setVisible(false);
  };

  useEffect(() => {
    setVisible(open);
    if (open) showError(null);
  }, [open, visible]);

  // The modal is never unmounted by its parents, so its state outlives a close.
  // A reopen therefore has to drop the previous visit's edit and re-seed, rather
  // than only clearing the flag — the applied code may not have changed, and the
  // sync effect above would then never fire.
  useEffect(() => {
    if (!open) return;
    dirtyRef.current = false;
    setCoupon(cartStore.currentOrder?.coupon_code ?? null);
    // Keyed on `open` alone on purpose. Adding the coupon code would clear
    // dirtyRef on a mid-visit order change and reintroduce DIS-1648.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const applyCoupon = async () => {
    showError(null);
    setSubmitting(true);
    // DIS-1608: await the apply. The old fire-and-forget close accepted a dead
    // code into the flow and lost the typed text.
    const result = await cartStore.updateCoupon(coupon);
    setSubmitting(false);
    if (!result.ok) {
      showError(result.message);
      return;
    }
    onClose();
  };

  const removeCoupon = async () => {
    showError(null);
    setSubmitting(true);
    const result = await cartStore.updateCoupon(null);
    setSubmitting(false);
    if (!result.ok) {
      showError(result.message);
      return;
    }
    setCoupon(null);
    onClose();
  };

  return (
    <CrossPlatformModal
      onBackdropPress={() => onClose()}
      isVisible={visible}
      style={styles.main}
      hasBackdrop
      backdropColor="black"
      backdropOpacity={0.5}
      avoidKeyboard
      keyboardAwareContent
      onBackButtonPress={() => onClose()}>
      <View style={[styles.modal, modalContainerStyle]}>
        <Text style={styles.title}>Add coupon code</Text>
        <View style={styles.border} />
        <TextInput
          testID="coupon-modal-input"
          containerStyle={styles.textInputContainer}
          inputContainerStyle={styles.textInputField}
          onChangeText={v => {
            dirtyRef.current = true;
            setCoupon(v);
          }}
          value={coupon}
          autoCapitalize="characters"
          placeholder="Enter coupon code"
        />
        <View style={styles.actions}>
          <Button
            title={
              coupon !== cartStore.currentOrder?.coupon_code && cartStore.currentOrder?.coupon_code
                ? 'CHANGE COUPON CODE'
                : 'ADD COUPON CODE'
            }
            onPress={() => applyCoupon()}
            loading={submitting}
            disabled={submitting || !coupon}
          />
          {cartStore.currentOrder?.coupon_code ? (
            <Button
              title="REMOVE COUPON"
              onPress={() => removeCoupon()}
              disabled={submitting}
              variant="outlined"
            />
          ) : null}
        </View>
        {!!error && (
          <Text testID="coupon-modal-error" style={styles.error}>
            {error}
          </Text>
        )}
      </View>
    </CrossPlatformModal>
  );
});

export default CouponModal;

const styles = StyleSheet.create({
  main: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    margin: 0,
  },
  modal: {
    backgroundColor: 'white',
    paddingHorizontal: 32,
    paddingVertical: 16,
    borderRadius: 4,
    borderWidth: 0.5,
    borderColor: 'grey',
  },
  title: {
    fontSize: 20,
    fontWeight: '600',
    color: theme.colour.text.heading.primary,
    textAlign: 'center',
  },
  border: {},
  textInputContainer: {
    width: '100%',
    paddingHorizontal: 0,
    marginTop: 16,
    marginBottom: 12,
  },
  textInputField: {
    width: '100%',
    borderWidth: 1,
    borderColor: 'lightgrey',
    borderRadius: 4,
    paddingHorizontal: 12,
    height: 44,
  },
  actions: {
    flexDirection: 'column',
    gap: 8,
  },
  error: {
    color: '#D8402E',
    fontSize: 13,
    marginTop: 8,
  },
});
